import Typography from "@mui/material/Typography";
import React, { useEffect, useState } from "react";
import LayerStore, { Layer } from "@giscon/utils.layer-store";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { observer } from "mobx-react";
import { observable, makeObservable, runInAction } from "mobx";
import RootStore from "SandboxViewerComponent/src/stores/RootStore";
import HelpLink from "./HelpLink";
import InfoIcon from "@mui/icons-material/Info";
import { Tooltip } from "@mui/material";
import AccordionPanel from "components/AccordionPanel";

class AdditionalLayerStore {
  constructor(players) {
    this.layers = (players || []).map((layer) => {
      const wrapper = new Layer(layer, LayerStore.map, LayerStore.view);
      wrapper.setChecked(false);
      return wrapper;
    });
    makeObservable(this, {
      layers: observable,
    });
  }

  addLayer(layer) {
    this.layers.push(layer);
  }

  removeLayer(layer) {
    let toRemove = -1;
    this.layers.forEach(({ name }, i) => {
      if (layer.name === name) {
        toRemove = i;
      }
    });
    if (toRemove !== -1) {
      this.layers.splice(toRemove, 1);
    }
  }
}

RootStore.additionalLayerStore = { store: null };
makeObservable(RootStore.additionalLayerStore, {
  store: observable.ref,
});

function AdditionalLayer({
  layers = [],
  nls = {
    title: "Zusätzliche Karten",
    // eslint-disable-next-line no-template-curly-in-string
    displayLayer: "${layer}",
  },
}) {
  const { title, displayLayer } = nls;
  const { map /*, view, token*/ } = LayerStore;
  const [store, setStore] = useState(() => new AdditionalLayerStore(layers));
  // const [showLayer, setShowLayer] = useState(true); // TODO: später default false und über checkbox unter den beiden autocompletes umschalten => diese checkbox nur anzeigen, wenn beide autocompletes ausgefüllt sind

  useEffect(() => {
    const tStore = new AdditionalLayerStore(layers);
    setStore(tStore);
    runInAction(() => {
      RootStore.additionalLayerStore.store = tStore;
    });
    return () => {
      // Cleanup must target the store created by THIS effect run.
      // React can run this cleanup after a newer render already replaced
      // RootStore.additionalLayerStore.store. If we used the global reference here,
      // we could accidentally remove layers from the newer store ("disappearing" layers).
      if (LayerStore.map) {
        tStore.layers.forEach((layer) => {
          layer.removeFromMap(LayerStore.map);
        });
      }

      // Only clear the global reference if it still points to this exact store.
      // Prevents an older cleanup from nulling out a newer store.
      if (RootStore.additionalLayerStore.store === tStore) {
        runInAction(() => {
          RootStore.additionalLayerStore.store = null;
        });
      }
    };
  }, [layers]);

  return (
    <AccordionPanel
      variant="sub"
      summary={
        <>
          <Typography variant="">{title}</Typography>
          <HelpLink
            className="help-link"
            size="20px"
            url="/hauptfunktionen/werkzeugleiste/zusaetzliche-karten"
          />
        </>
      }
    >
      <>
        {store.layers.map((layer, i) => {
          const {
            minScale,
            maxScale,
            hasScaleRestrictions, // TODO: check if it's hasScaleRestrictions or hasScaleRestriction
            visibleAtMapScale,
            id,
          } = layer;
          let title = "";

          if (hasScaleRestrictions) {
            if (minScale !== 0 && maxScale !== 0) {
              title = `Dieser Layer wird nur bei einer Skalierung zwischen 1:${minScale} und 1:${maxScale} dargestellt.`;
            } else if (minScale !== 0) {
              title = `Dieser Layer wird nur ab einer Skalierung von 1:${minScale} dargestellt.`;
            } else {
              title = `Dieser Layer wird nur bis zu einer Skalierung von 1:${maxScale} dargestellt.`;
            }
          }

          return (
            <div style={{ width: "100%" }} key={layer?.url + i}>
              <FormControlLabel
                value="end"
                style={{
                  marginLeft: "-8px",
                  opacity: visibleAtMapScale ? 1 : 0.75,
                }}
                control={
                  <Checkbox
                    checked={layer.checked}
                    color="default"
                    onChange={(e) => {
                      layer.setChecked(e.target.checked);
                      if (e.target.checked) {
                        layer.addToMap(map);
                      } else {
                        layer.removeFromMap(map);
                      }
                    }}
                  />
                }
                // eslint-disable-next-line no-template-curly-in-string
                label={displayLayer.replace("${layer}", layer.id)}
                labelPlacement="end"
              />
              {hasScaleRestrictions && (
                <Tooltip title={title}>
                  <InfoIcon
                    style={{
                      position: "absolute",
                      marginTop: 9,
                      right: 50,
                    }}
                    placement="top"
                  />
                </Tooltip>
              )}
            </div>
          );
        })}
      </>
    </AccordionPanel>
  );
}

export default observer(AdditionalLayer);
